import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Checkbox, commonUtils, Label, useToast } from '@sato/components';
import * as yup from 'yup';

import { IWidget, ParsedEntry } from '@/contentful/types';

import { api } from '@api';
import { HookFormCheckboxGroup } from '@components/ReactHookFormFields/HookFormCheckboxGroup';
import { HookFormDropdown } from '@components/ReactHookFormFields/HookFormDropdown';
import { HookFormTextArea } from '@components/ReactHookFormFields/HookFormTextArea';
import { useIncidentCreation } from '@hooks/incidents';
import { useCustomTranslations } from '@hooks/useCustomTranslations';
import {
  IncidentClassification,
  IncidentSubClassification,
} from '@models/Incident';
import { apartmentStore } from '@stores/apartmentStore';
import { t } from '@stores/localization';
import { requiredSuffix } from '@utils/text.utils';

import { IncidentFormWrapper } from '../IncidentFormWrapper';

import { ColorCheckboxCard } from './components/ColorCheckboxCard';
import { ColorSelectionsSummary } from './components/ColorSelectionsSummary';
import {
  allPaints,
  colorPaints,
  whitePaints,
} from './RenovationPaintingKitForm.utils';

const colorPaintColorOptions = colorPaints.map((paint) => paint.value);
const whitePaintColorOptions = whitePaints.map((paint) => paint.value);

export const RenovationPaintingKitForm = ({
  parameters,
  customTranslations,
}: ParsedEntry<IWidget>) => {
  const { toastsComponent, addToast } = useToast();
  const { t: customT } = useCustomTranslations(customTranslations);

  const { create } = useIncidentCreation({
    postIncident: api.incidents.postApartmentMaintenanceIncident,
    classifications: {
      classification: IncidentClassification.APARTMENT_CONDITION,
      subClassification: IncidentSubClassification.PAINTING,
    },
  });

  const rooms = apartmentStore.selectedApartment?.apartment.amountOfRooms ?? 0;

  const isLargeApartment = rooms >= 3;

  return (
    <IncidentFormWrapper
      incidentKind="RenovationPaintingKit"
      defaultValues={{
        description: '',
        pickupLocation: '',
      }}
      validationSchema={yup.object().shape({
        pickupLocation: yup.string().required(t('error.required_field')),
        description: yup.string(),
        whiteShades: yup.array().min(1, t('error.required_field')).required(),
        colorShades: yup.array().min(1, t('error.required_field')).required(),
        useCasesForPaints: yup.object(),
      })}
      onSubmit={async ({
        pickupLocation,
        description,
        colorShades,
        useCasesForPaints,
        whiteShades,
      }) => {
        const selectedPaintKeys = [...colorShades, ...whiteShades].filter(
          (shade) => commonUtils.isTruthy(shade) && shade !== 'optOut'
        );

        const selectedPaintShades = selectedPaintKeys.map((key) => ({
          ...allPaints.find((paint) => key === paint.value)!,
          // Include useCase for each paint
          useCase:
            key in useCasesForPaints
              ? (useCasesForPaints as Record<string, string>)[key]
              : undefined,
        }));

        // Error alert if use case is not defined for each selected paint shade
        if (selectedPaintShades.some((shade) => !shade.useCase)) {
          addToast({
            message: t('renovation.missingPaintUseCasesError'),
            variant: 'alert',
            duration: 5000,
          });

          return;
        }

        return create({
          title: customT('messageTitle'),
          description: t('renovation.paintOrderMessageTemplate', {
            paintShadesList: selectedPaintShades.length
              ? selectedPaintShades
                  .map((color) =>
                    t('renovation.paintOrderMessageTemplate.paintItem', {
                      paintShade: [color.value, color.label].join(' '),
                      useCase: color.useCase,
                    })
                  )
                  .join('\n\n')
              : t('renovation.paintOrderMessageTemplate.omitPaintsFromOrder'),
            pickupLocation,
            description,
          }),
        });
      }}
    >
      <div className="flex flex-col gap-3">
        <Label component="h4">
          {requiredSuffix(t('renovation.colorPaintDescription', { rooms }))}
        </Label>

        <HookFormCheckboxGroup
          name="colorShades"
          alignment="vertical"
          maxSelections={isLargeApartment ? 3 : 1} // Allow max 3 colors for large apartments, else 1
        >
          <ClearColorSelectionCheckbox
            label={customT('noColorPaint')}
            affectedField="colorShades"
          />

          <div className="grid w-full grid-cols-1 gap-2 md:grid-cols-2 md:gap-3 lg:grid-cols-3 xl:grid-cols-4">
            {colorPaintColorOptions.map((option) => (
              <ColorCheckboxCard
                key={option}
                {...colorPaints.find((paint) => paint.value === option)!}
              />
            ))}
          </div>
        </HookFormCheckboxGroup>
      </div>

      <div className="flex flex-col gap-3">
        <Label component="h4">
          {requiredSuffix(t('renovation.whitePaintDescription'))}
        </Label>

        <HookFormCheckboxGroup
          name="whiteShades"
          alignment="vertical"
          maxSelections={1} // Allow only 1 white shade selection
        >
          <ClearColorSelectionCheckbox
            label={customT('noWhitePaint')}
            affectedField="whiteShades"
          />

          <div className="grid w-full grid-cols-1 gap-2 md:grid-cols-2 md:gap-3 lg:grid-cols-3 xl:grid-cols-4">
            {whitePaintColorOptions.map((option) => (
              <ColorCheckboxCard
                key={option}
                {...whitePaints.find((paint) => paint.value === option)!}
              />
            ))}
          </div>
        </HookFormCheckboxGroup>
      </div>

      <ColorSelectionsSummary className="my-5" />

      <HookFormDropdown
        floatingLabel
        name="pickupLocation"
        items={
          parameters?.kRautaLocations?.map(({ value }: { value: string }) => ({
            label: value,
            value,
          })) || []
        }
        label={requiredSuffix(customT('selectLabel'))}
      />

      <HookFormTextArea
        name="description"
        type="text"
        rows={6}
        placeholder={customT('descriptionPlaceholder')}
        label={{
          type: 'static',
          value: customT('descriptionLabel'),
        }}
        maxLength={600}
      />

      {!!toastsComponent && (
        <div className="flex w-full flex-col items-center gap-3">
          {toastsComponent}
        </div>
      )}
    </IncidentFormWrapper>
  );
};

/** Checkbox for opting out and clearing paint color selections */
const ClearColorSelectionCheckbox = ({
  affectedField,
  label,
}: {
  affectedField: 'colorShades' | 'whiteShades';
  label: string;
}) => {
  const { setValue, watch } = useFormContext<{
    colorShades: string[] | undefined;
    whiteShades: string[] | undefined;
  }>();

  const fieldValue = watch(affectedField);

  // If a paint color selection is made while "optOut" is selected, clear optOut selection
  useEffect(() => {
    if ((fieldValue?.length ?? 0) > 1 && fieldValue?.includes('optOut')) {
      requestAnimationFrame(() => {
        setValue(
          affectedField,
          fieldValue.filter((value) => value !== 'optOut')
        );
      });
    }
  }, [fieldValue]);

  return (
    <Checkbox
      label={label}
      value="optOut"
      onChange={(value) => {
        if (!value) return;
        // If opting out, clear all other color selections
        setValue(affectedField, ['optOut']);
      }}
      className="w-full"
    />
  );
};
